import * as React from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Checkbox
} from "../../components/mui";
import {
  SearchRoundedIcon,
  RefreshRoundedIcon,
  GetAppRoundedIcon,
  HistoryRoundedIcon,
  EditRoundedIcon
} from "../../components/icons";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";

import PageLayout from "../../components/shared/PageLayout";
import PageHeader from "../../components/shared/PageHeader";
import DataTableCard from "../../components/shared/DataTableCard";
import { useToast } from "../../components/shared/ToastProvider";
import ExpensesModal from "./modals/ExpensesModal";
import UpdateReceivedModal from "./modals/UpdateReceivedModal";
import UpdatePaymentModal from "./modals/UpdatePaymentModal";

import "../../styles/pages/rfHistory.css";

// Hardcoded filter options - Backend developer will replace with API calls
const FILTER_OPTIONS = {
  type: [
    { value: "all", label: "All" },
    { value: "log", label: "LOG" },
    { value: "e-mail", label: "E-mail" },
    { value: "mapping", label: "Mapping" }
  ],
  condo: [
    { value: "all", label: "All" },
    { value: "dasmari", label: "Dasmarinas Village" },
    { value: "jazz", label: "Jazz Residences" }
  ],
  condoUnit: [
    { value: "all", label: "All" }
  ],
  condoCompany: [
    { value: "all", label: "All" },
    { value: "none", label: "None selected" }
  ]
};

// Hardcoded RF data - Backend developer will replace with API calls
const RF_DATA = [
  {
    id: 1,
    type: "Utility",
    rfNo: "12035",
    mapping: "Mapping",
    currency: "PHP",
    particulars: {
      description: {
        address: "Dasmarinas Village - 2368 Macola St.",
        utility: "Electricity (1st Shared)",
        dateRange: "24/11/2025 - 26/12/2025"
      },
      qty: "1",
      unitCost: "27,310.39"
    },
    total: "27,310.39",
    paymentTerms: "Cash",
    paymentInfo: {
      amountPaid: "0.00",
      checkNo: "-",
      acctEntryDate: "-",
      paymentDate: "-",
      checkReleased: "No",
      releasedOn: "-",
      releasedBy: "-"
    },
    vendor: "Meralco",
    dateRequested: "08/01/2026",
    requestedBy: "Arlene Kam",
    received: {
      by: "Hew Chin Chew",
      on: "23/03/2012"
    },
    remarks: "-"
  },
  {
    id: 2,
    type: "Utility",
    rfNo: "12034",
    mapping: "Mapping",
    currency: "PHP",
    particulars: {
      description: {
        address: "Dasmarinas Village - 2368 Macola St.",
        utility: "PLDT (Not Shared)",
        dateRange: "05/12/2025 - 06/01/2026"
      },
      qty: "1",
      unitCost: "2,699.00"
    },
    total: "2,699.00",
    paymentTerms: "Cash",
    paymentInfo: {
      amountPaid: "0.00",
      checkNo: "-",
      acctEntryDate: "-",
      paymentDate: "-",
      checkReleased: "No",
      releasedOn: "-",
      releasedBy: "-"
    },
    vendor: "PLDT",
    dateRequested: "29/12/2025",
    requestedBy: "Arlene Kam",
    received: {
      by: "Hew Chin Chew",
      on: "23/03/2012"
    },
    remarks: "-"
  },
  {
    id: 3,
    type: "Utility",
    rfNo: "12033",
    mapping: "Mapping",
    currency: "PHP",
    particulars: {
      description: {
        address: "Dasmarinas Village - 2368 Macola St.",
        utility: "Water (Not Shared)",
        dateRange: "17/11/2025 - 16/12/2025"
      },
      qty: "1",
      unitCost: "10,860.42"
    },
    total: "10,860.42",
    paymentTerms: "Cash",
    paymentInfo: {
      amountPaid: "0.00",
      checkNo: "-",
      acctEntryDate: "-",
      paymentDate: "-",
      checkReleased: "No",
      releasedOn: "-",
      releasedBy: "-"
    },
    vendor: "Manila Water",
    dateRequested: "29/12/2025",
    requestedBy: "Arlene Kam",
    received: {
      by: "Hew Chin Chew",
      on: "23/03/2012"
    },
    remarks: "-"
  },
  {
    id: 4,
    type: "Misc",
    rfNo: "12032",
    mapping: "Mapping",
    currency: "PHP",
    particulars: {
      description: {
        address: "Jazz Residences - 1024 Jazz Residences",
        utility: "Repairs & Maintenance - Aircon Repair (Not Shared)",
        dateRange: "23/11/2025 - 23/11/2025"
      },
      qty: "1",
      unitCost: "1,000.00"
    },
    total: "1,000.00",
    paymentTerms: "Cash",
    paymentInfo: {
      amountPaid: "0.00",
      checkNo: "-",
      acctEntryDate: "-",
      paymentDate: "-",
      checkReleased: "No",
      releasedOn: "-",
      releasedBy: "-"
    },
    vendor: "Tom Christopher Lag...",
    dateRequested: "01/12/2025",
    requestedBy: "Arlene Kam",
    received: null,
    remarks: "-"
  }
];

/**
 * Condo RF History Page Component
 * Displays RF (Request Form) history with filters and table
 * UI/Layout only - Backend developer will add:
 * - API calls for fetching RF data based on filters
 * - Edit RF functionality
 * - Export functionality
 * - Pagination
 */
export default function RfHistory({ onLogout, onNavigate }) {
  const { showToast } = useToast();

  const [filters, setFilters] = React.useState({
    type: "all",
    requestNoFrom: "",
    requestNoTo: "",
    condo: "all",
    condoUnit: "all",
    condoCompany: "all"
  });

  const [expensesModalOpen, setExpensesModalOpen] = React.useState(false);
  const [updateReceivedModalOpen, setUpdateReceivedModalOpen] = React.useState(false);
  const [updatePaymentModalOpen, setUpdatePaymentModalOpen] = React.useState(false);
  const [selectedRfData, setSelectedRfData] = React.useState(null);


  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    showToast({
      message: "RF history loaded successfully",
      severity: "success"
    });
  };

  const handleReset = () => {
    setFilters({
      type: "all",
      requestNoFrom: "",
      requestNoTo: "",
      condo: "all",
      condoUnit: "all",
      condoCompany: "all"
    });
  };

  const handleExport = () => {
    showToast({
      message: "Exporting RF history...",
      severity: "info"
    });
  };

  const handleExportFinance = () => {
    showToast({
      message: "Exporting RF history for finance...",
      severity: "info"
    });
  };


  const handleEdit = (rfNo) => {
    // Navigate to edit RF page
    onNavigate({ key: "edit-rf" });
  };

  const handleEmail = (rfNo) => {
    showToast({
      message: "Email successfully sent",
      severity: "success"
    });
  };

  const handleMapping = (rfData) => {
    setSelectedRfData(rfData);
    setExpensesModalOpen(true);
  };

  const handleUpdateReceived = (rfData) => {
    setSelectedRfData(rfData);
    setUpdateReceivedModalOpen(true);
  };

  const handleUpdatePayment = (rfData) => {
    setSelectedRfData(rfData);
    setUpdatePaymentModalOpen(true);
  };

  return (
    <PageLayout activeKey="rf-history" onLogout={onLogout} onNavigate={onNavigate} className="page-content">
      <PageHeader
        icon={HistoryRoundedIcon}
        title="Condo RF History"
        summary="View and manage all condo request form history."
      />

      {/* Filters */}
      <Box className="content-box rf-history-filters">
        <Box className="rf-history-filters-row">
          <FormControl size="small" className="rf-history-filter">
            <InputLabel>Type</InputLabel>
            <Select value={filters.type} label="Type" onChange={handleFilterChange("type")}>
              {FILTER_OPTIONS.type.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Request No From"
            value={filters.requestNoFrom}
            onChange={handleFilterChange("requestNoFrom")}
            className="rf-history-filter"
          />

          <TextField
            size="small"
            label="Request No To"
            value={filters.requestNoTo}
            onChange={handleFilterChange("requestNoTo")}
            className="rf-history-filter"
          />

          <FormControl size="small" className="rf-history-filter">
            <InputLabel>Condo</InputLabel>
            <Select value={filters.condo} label="Condo" onChange={handleFilterChange("condo")}>
              {FILTER_OPTIONS.condo.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" className="rf-history-filter">
            <InputLabel>Condo Unit</InputLabel>
            <Select value={filters.condoUnit} label="Condo Unit" onChange={handleFilterChange("condoUnit")}>
              {FILTER_OPTIONS.condoUnit.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" className="rf-history-filter">
            <InputLabel>Condo Company</InputLabel>
            <Select value={filters.condoCompany} label="Condo Company" onChange={handleFilterChange("condoCompany")}>
              {FILTER_OPTIONS.condoCompany.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box className="rf-history-actions">
          <Button variant="contained" startIcon={<SearchRoundedIcon />} onClick={handleSearch}>
            Search
          </Button>
          <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={handleReset}>
            Reset
          </Button>
          <Button variant="outlined" startIcon={<GetAppRoundedIcon />} onClick={handleExport}>
            Export
          </Button>
          <Button variant="outlined" startIcon={<GetAppRoundedIcon />} onClick={handleExportFinance}>
            Export (Finance)
          </Button>
        </Box>
      </Box>

      {/* Data Table */}
      <DataTableCard
        title="RF Records"
        showExport={false}
        showEntriesControl={true}
        showFooter={true}
        totalEntries={RF_DATA.length}
        startEntry={1}
        endEntry={RF_DATA.length}
        currentPage={1}
        totalPages={1}
      >
        <TableContainer className="data-table-wrapper">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2}>Actions</TableCell>
                <TableCell rowSpan={2}>RF No</TableCell>
                <TableCell rowSpan={2}>Type</TableCell>
                <TableCell rowSpan={2}>Currency</TableCell>
                <TableCell colSpan={3} align="center">Particulars</TableCell>
                <TableCell rowSpan={2} align="right">Total</TableCell>
                <TableCell rowSpan={2}>Payment Terms</TableCell>
                <TableCell rowSpan={2}>Payment Info</TableCell>
                <TableCell rowSpan={2}>Vendor</TableCell>
                <TableCell rowSpan={2}>Date Requested</TableCell>
                <TableCell rowSpan={2}>Requested By</TableCell>
                <TableCell rowSpan={2}>Received</TableCell>
                <TableCell rowSpan={2}>Remarks</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Unit Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {RF_DATA.map((row) => (
                <TableRow hover key={row.id}>
                  <TableCell>
                    <Box className="rf-history-actions-cell">
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<EditRoundedIcon />}
                        onClick={() => handleEdit(row.rfNo)}
                        className="rf-history-action-btn"
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<EmailRoundedIcon />}
                        onClick={() => handleEmail(row.rfNo)}
                        className="rf-history-action-btn"
                      >
                        Send Email
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box className="rf-history-rfno-cell">
                      <div>{row.rfNo}</div>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<LinkRoundedIcon />}
                        className="rf-history-mapping-btn"
                        onClick={() => handleMapping(row)}
                      >
                        {row.mapping}
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>{row.currency}</TableCell>
                  <TableCell className="rf-history-description">
                    <Box className="rf-history-description-lines">
                      <div>{row.particulars.description.address}</div>
                      <div>{row.particulars.description.utility}</div>
                      <div>{row.particulars.description.dateRange}</div>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{row.particulars.qty}</TableCell>
                  <TableCell align="right">{row.particulars.unitCost}</TableCell>
                  <TableCell align="right">{row.total}</TableCell>
                  <TableCell>{row.paymentTerms}</TableCell>
                  <TableCell>
                    <Box className="rf-history-payment-info">
                      <div className="rf-history-info-row">
                        <span className="rf-history-info-label">Amount Paid</span>
                        <span className="rf-history-info-value">{row.paymentInfo.amountPaid}</span>
                      </div>
                      <div className="rf-history-info-row">
                        <span className="rf-history-info-label">Check No</span>
                        <span className="rf-history-info-value">{row.paymentInfo.checkNo}</span>
                      </div>
                      <div className="rf-history-info-row">
                        <span className="rf-history-info-label">Acct Entry Date</span>
                        <span className="rf-history-info-value">{row.paymentInfo.acctEntryDate}</span>
                      </div>
                      <div className="rf-history-info-row">
                        <span className="rf-history-info-label">Payment Date</span>
                        <span className="rf-history-info-value">{row.paymentInfo.paymentDate}</span>
                      </div>
                      <div className="rf-history-info-row">
                        <span className="rf-history-info-label">Check Released</span>
                        <span className="rf-history-info-value">{row.paymentInfo.checkReleased}</span>
                      </div>
                      <div className="rf-history-info-row">
                        <span className="rf-history-info-label">Released On</span>
                        <span className="rf-history-info-value">{row.paymentInfo.releasedOn}</span>
                      </div>
                      <div className="rf-history-info-row">
                        <span className="rf-history-info-label">Released By</span>
                        <span className="rf-history-info-value">{row.paymentInfo.releasedBy}</span>
                      </div>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<EditRoundedIcon />}
                        className="rf-history-edit-link"
                        onClick={() => handleUpdatePayment(row)}
                      >
                        Edit
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell>{row.vendor}</TableCell>
                  <TableCell>{row.dateRequested}</TableCell>
                  <TableCell>{row.requestedBy}</TableCell>
                  <TableCell>
                    <Box className="rf-history-received-info">
                      {row.received ? (
                        <>
                          <div>By {row.received.by}</div>
                          <div>on {row.received.on}</div>
                        </>
                      ) : (
                        <span>-</span>
                      )}
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<EditRoundedIcon />}
                        className="rf-history-edit-link"
                        onClick={() => handleUpdateReceived(row)}
                      >
                        Edit
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell>{row.remarks}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DataTableCard>

      {/* Expenses Modal */}
      <ExpensesModal
        open={expensesModalOpen}
        onClose={() => setExpensesModalOpen(false)}
        rfData={selectedRfData}
      />

      {/* Update Received Modal */}
      <UpdateReceivedModal
        open={updateReceivedModalOpen}
        onClose={() => setUpdateReceivedModalOpen(false)}
        rfData={selectedRfData}
      />

      {/* Update Payment Modal */}
      <UpdatePaymentModal
        open={updatePaymentModalOpen}
        onClose={() => setUpdatePaymentModalOpen(false)}
        rfData={selectedRfData}
      />
    </PageLayout>
  );
}
